import React, { FormEvent, useEffect, useState } from 'react'
import { Alert, Button, IconButton, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { Form } from 'react-router-dom';

import { ICustomer, ICustomerCategory } from '../../types/customer';

import { CustomerService } from '../../services/customerService';

import { Icons } from '../icons';
import Json from '../json';

import '../../styles/customerCategoriesForm.scss';
import { VendorService } from '../../services/vendorService';
import { ISelectOption } from '../../types/list';


export interface ICustomerCategoriesFormProps {
	customer: ICustomer;
	refreshCallback: () => void;
}

const CustomerCategoriesForm: React.FunctionComponent<ICustomerCategoriesFormProps> = ({ customer, refreshCallback }) => {
	const [categoryView, setCategoryView] = useState<ICustomerCategory[]>([...customer.categories ?? []]);
	const [formFields, setFormFields] = useState<ICustomerCategory>({
		id: undefined,
		name: '',
		vendorId: -1,
		customerId: Number.parseInt(customer.id)
	});
	const [isAdding, setIsAdding] = useState(false);
	const [isDeactivatingIds, setIsDeactivatingIds] = useState<number[]>([]);
	const [error, setError] = useState<string | undefined>();
	const [vendorOptions, setVendorOptions] = useState<ISelectOption[]>([]);
	const [isLoadingVendors, setIsLoadingVendors] = useState(false);

	useEffect(() => {
		setIsLoadingVendors(true);

		VendorService.getVendorsByCustomerId(parseInt(customer.id))
		.then((response) => {
			console.log('VendorService.getVendorsByCustomerId: SUCCESS', response);

			let vendors: ISelectOption[] = [];
			response.data.map((item) => {
				if(item.status == "Active"){
					vendors.push({
						name: item.name,
						value: item.id
					})
				}
			});
			setVendorOptions(vendors.sort((a,b) => a.name > b.name ? 1 : -1));
			setFormFields({ ...formFields, ["vendorId"]: parseInt(vendors[0].value) })
		})
		.catch((error) => {
			console.log('VendorService.getVendorsByCustomerId: ERROR', error);

		})
		.finally(() => {
			setIsLoadingVendors(false);
		});
	}, []);

	useEffect(() => {
		setCategoryView(customer.categories ?? []);
	}, [customer])
	
	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setIsAdding(true);
		setError(undefined);

		if (categoryView
			.some(c => 
				c.name.toLowerCase().trim() == formFields.name.toLowerCase().trim() &&
		    	c.vendorId == formFields.vendorId &&
		    	c.isActive)) {
			setError("A customer code with this name and vendor combination already exists.");
			setIsAdding(false);
			return;
		}

		CustomerService.addCategory(customer.id, formFields)
			.then((response) => {
				console.log('CustomerService.addCategory: SUCCESS', response);				
				setFormFields({
					id: undefined,
					name: "",
					vendorId: parseInt(vendorOptions[0].value),
					customerId: Number.parseInt(customer.id)
				});
				setCategoryView([...categoryView, response.data]);
				refreshCallback();
			})
			.catch((error) => {
				console.log('CustomerService.addCategory: ERROR', error);
				setError("We encountered an error while processing your request. Please try again.");
			})
			.finally(() => {
				setIsAdding(false);
			});
	}

	const handleRemoveCategory = (id: number | undefined) => {
		if (id) {
			setError(undefined);
			setIsDeactivatingIds(isDeactivatingIds => [...isDeactivatingIds, id]);

			CustomerService.deactivateCategory(id)
				.then((response) => {
					console.log('CustomerService.deactivateCategory: SUCCESS', response);
					setCategoryView(categoryView.filter(c => c.id != id));
					refreshCallback();
				})
				.catch((error) => {
					console.log('CustomerService.deactivateCategory: ERROR', error);
					setError("We encountered an error while processing your request. Please try again.");
				})
				.finally(() => {
					setIsDeactivatingIds(isDeactivatingIds.filter((item) => { return item != id }));
				});
		}
	}

	const isDeactivateDisabled = (id: number | undefined) => {
		if (id && isDeactivatingIds.indexOf(id) >= 0) {
			return true;
		}
		return false;
	}

	return (
		<section className="hwi-customer-categories-form">
			<div className="results-table">
				<div className="headers">
					<div className="flex-2">
						<p>
							Code
						</p>
					</div>
					<div className="flex-2">
						<p>
							Vendor
						</p>
					</div>
					<div className="flex-1">{""}</div>
				</div>
				<div className="data">
					{categoryView && categoryView.filter((item) => item.isActive).length > 0 ?
						(
							categoryView.filter((item) => item.isActive).map((item, index) => (
								<div className="row" key={index}>
									<div className="flex-2"><p>{item.name}</p></div>
									<div className="flex-2"><p>{item.vendorName}</p></div>
									<div className="controls flex-1">
										<p>
											<Button onClick={() => handleRemoveCategory(item.id)} disabled={isAdding} >
												{isDeactivateDisabled(item.id) ? "Deactivating..." : (
													<>
														<Icons.RemoveItem fill="#a82525" height="16px"></Icons.RemoveItem>
														<span>Deactivate</span>
													</>
												)}
											</Button>
										</p>
									</div>
								</div>
							))
						) : (<div className="no-results">0 results</div>)}
				</div>
				{error &&
					<Alert className="alert-error" severity="error" action={<IconButton onClick={() => { setError(undefined); }} size='small'>X</IconButton>}>{error}</Alert>
				}
				<Form onSubmit={(event) => handleSubmit(event)}>
					<div className="row">
						<div>
						<InputLabel>Code</InputLabel>
							<TextField className="name-field" name="name" required placeholder='Enter code name' variant="outlined" value={formFields.name} size="small" onChange={(event) => { setFormFields({ ...formFields, ["name"]: event.target.value }) }}></TextField>
						</div>
						<div>
						<InputLabel>Vendor</InputLabel>
							<Select
								size="small"
								className="vendor-field"
								value={formFields.vendorId == -1 ? '' : formFields.vendorId}
								required
								fullWidth={true}
								onChange={(event) => { setFormFields({ ...formFields, ["vendorId"]: Number.parseInt(event.target.value.toString()) }) }}
							>
								{vendorOptions.map((item, index) => (
									<MenuItem value={item.value} key={index}>{item.name}</MenuItem>
								))}
							</Select>
						</div>
						<div className="controls">
							<p>
								<Button type="submit" className="save-button" disabled={isAdding || isLoadingVendors || !formFields.name} variant="text">
									{isAdding ? "Saving..." : (
										<>
											<Icons.AddItem fill="#434D76" height="16px"></Icons.AddItem>
											<span>Add Code</span>
										</>
									)}
								</Button>
							</p>
						</div>
					</div>
				</Form>
			</div>
		</section>
	)
}

export default CustomerCategoriesForm;

